/**
 * «Descargar resumen» — el resumen mensual OFICIAL del cliente en PDF o Excel (round 2026-10-29).
 *
 * Vive en la fila de filtros de /finanzas-cliente, junto al selector de mes y «Ver mes anterior». Es un botón con menú:
 *   - sin más datos: PDF / Excel (el documento trae todas las propiedades publicadas del cliente);
 *   - si el cliente tiene más de una propiedad y hay una seleccionada: dos grupos, «Todas mis propiedades» y «Solo
 *     <PROPIEDAD>», cada uno con PDF / Excel.
 *
 * Todo lo importante lo decide el SERVIDOR: este botón no sabe si el mes está publicado (por eso un 409 sale con su
 * aviso y no se deshabilita de antemano) ni arma números. Lo que sí hace:
 *   - Administrador: manda `clienteId` = el dueño de la propiedad seleccionada (la lista del administrador trae TODAS
 *     las propiedades de la empresa y su fila incluye `id_cliente`). Un cliente nunca manda `clienteId`: el servidor lo
 *     ignora y usa el de su sesión. «Todas las propiedades» del administrador son las de ESE cliente.
 *   - Un estado de carga por formato: mientras se genera el PDF, ese ítem queda deshabilitado (un doble clic no pide dos
 *     documentos ni gasta el cupo de descargas), pero el Excel sigue disponible.
 *   - Sin mes o sin propiedad (o si al administrador no le consta el cliente) el botón se ve deshabilitado, pero es
 *     `aria-disabled` y NO `disabled`: un botón deshabilitado de verdad no recibe foco, y entonces quien navega con teclado
 *     o con lector de pantalla nunca se entera de POR QUÉ no puede descargar. Así recibe foco, el tooltip aparece con el
 *     foco además del mouse, y el motivo queda como descripción accesible (`aria-describedby` a un texto oculto a la vista).
 *     El menú no se abre mientras falte algo (el `Dropdown` queda sin disparadores).
 *   - Un aviso `role="status"` (`aria-live="polite"`) anuncia «Generando el PDF…» y el resultado: `aria-busy` casi ningún
 *     lector lo lee, y el toast final dura unos segundos.
 *   - Mensajes de error legibles, ya traducidos por el servicio (`ErrorDescargaResumen`).
 *
 * Presentación: el nombre de la propiedad va en MAYÚSCULAS solo al mostrarlo (`formatPropertyName`).
 */
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Button, Dropdown, Tooltip, message } from "antd";
import type { MenuProps } from "antd";
import { DownloadOutlined, FileExcelOutlined, FilePdfOutlined, LoadingOutlined } from "@ant-design/icons";
import type { Dayjs } from "dayjs";
import { getMsg } from "../../utils/antdStatic";
import { formatPropertyName } from "../../utils/propertyDisplay";
import {
  descargarResumenMensual,
  ErrorDescargaResumen,
  MENSAJES_RESUMEN,
} from "../../services/resumenMensual.api";
import type { FormatoResumen } from "../../services/resumenMensual.api";
import type { ClienteProp } from "./useFinanzasClientesLogic";

type Alcance = "todas" | "una";

export interface DescargarResumenProps {
  /** Mes elegido en el selector de la pantalla. */
  mes: Dayjs | null;
  /** Lista de propiedades de la pantalla: las del cliente, o TODAS si quien mira es administrador. */
  propiedades: ClienteProp[];
  /** Propiedad seleccionada en la pantalla. */
  propiedadId: number | null;
  esAdmin: boolean;
}

const avisar = (tipo: "success" | "error", content: string) =>
  (getMsg() ?? message)[tipo]({ content, key: "descarga-resumen" });

/** Nombre del formato como lo lee la persona. */
const NOMBRE_FORMATO: Record<FormatoResumen, string> = { pdf: "PDF", xlsx: "Excel" };

/**
 * «Solo para lectores de pantalla»: invisible, pero presente en el árbol de accesibilidad (a diferencia de `display:none`,
 * que lo saca). Es la técnica clásica de recorte de 1 px; va en línea para no depender de una hoja de estilos.
 */
const SOLO_LECTOR: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  margin: -1,
  padding: 0,
  border: 0,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

/** Cuánto queda el último aviso del lector de pantalla antes de limpiarse (no debe quedar «Resumen descargado.» eternamente). */
const MS_AVISO_LECTOR = 8000;

const DescargarResumen: React.FC<DescargarResumenProps> = ({ mes, propiedades, propiedadId, esAdmin }) => {
  // Formatos con una descarga en curso. El `ref` es la puerta (síncrona); el estado solo pinta.
  const enCurso = useRef<Set<FormatoResumen>>(new Set());
  const [cargando, setCargando] = useState<ReadonlySet<FormatoResumen>>(new Set());
  // Texto de la región `aria-live`: lo que se anuncia al lector de pantalla (progreso y resultado).
  const [anuncio, setAnuncio] = useState("");
  const idMotivo = useId();

  // El último aviso se limpia solo cuando ya no hay nada en curso.
  useEffect(() => {
    if (!anuncio || cargando.size > 0) return;
    const t = setTimeout(() => setAnuncio(""), MS_AVISO_LECTOR);
    return () => clearTimeout(t);
  }, [anuncio, cargando]);

  const seleccionada = useMemo(
    () => propiedades.find((p) => p.id === propiedadId) ?? null,
    [propiedades, propiedadId],
  );
  // Administrador: el cliente es el dueño de la propiedad seleccionada. Cliente: no se envía (el servidor usa su sesión).
  const clienteId = esAdmin ? (seleccionada?.id_cliente ?? null) : null;
  const delCliente = useMemo(
    () => (esAdmin ? (clienteId != null ? propiedades.filter((p) => p.id_cliente === clienteId) : []) : propiedades),
    [esAdmin, clienteId, propiedades],
  );
  const ofreceAlcance = !!seleccionada && delCliente.length > 1;
  const periodo = mes ? mes.format("YYYY-MM") : null;

  const motivoDeshabilitado = !periodo
    ? "Elige un mes para descargar el resumen."
    : !seleccionada
      ? "Elige una propiedad para descargar el resumen."
      : esAdmin && clienteId == null
        ? "No se pudo identificar al cliente de esta propiedad."
        : null;

  const descargar = async (alcance: Alcance, formato: FormatoResumen) => {
    if (!periodo || !seleccionada || enCurso.current.has(formato)) return;
    enCurso.current.add(formato);
    setCargando(new Set(enCurso.current));
    setAnuncio(`Generando el ${NOMBRE_FORMATO[formato]}…`);
    try {
      await descargarResumenMensual({
        mes: periodo,
        formato,
        propId: alcance === "una" ? seleccionada.id : null,
        clienteId,
        nombrePropiedad: seleccionada.nombrepropiedad ?? null,
      });
      avisar("success", "Resumen descargado.");
      // Distinto del texto del toast a propósito: el toast ya se lee solo (AntD le pone `role="alert"`) y repetirlo
      // palabra por palabra haría que el lector lo diga dos veces; aquí se agrega CUÁL formato quedó listo.
      setAnuncio(`Listo, el ${NOMBRE_FORMATO[formato]} se descargó.`);
    } catch (e) {
      const texto = e instanceof ErrorDescargaResumen ? e.message : MENSAJES_RESUMEN.servidor;
      avisar("error", texto);
      setAnuncio(texto);
    } finally {
      enCurso.current.delete(formato);
      setCargando(new Set(enCurso.current));
    }
  };

  const formatos = (alcance: Alcance): NonNullable<MenuProps["items"]> => [
    {
      key: `${alcance}:pdf`,
      icon: <FilePdfOutlined />,
      label: "PDF",
      disabled: cargando.has("pdf"),
    },
    {
      key: `${alcance}:xlsx`,
      icon: <FileExcelOutlined />,
      label: "Excel",
      disabled: cargando.has("xlsx"),
    },
  ];

  const items: MenuProps["items"] = ofreceAlcance
    ? [
        {
          type: "group",
          label: esAdmin ? "Todas las propiedades del cliente" : "Todas mis propiedades",
          children: formatos("todas"),
        },
        {
          type: "group",
          label: `Solo ${formatPropertyName(seleccionada?.nombrepropiedad) || `propiedad #${seleccionada?.id}`}`,
          children: formatos("una"),
        },
      ]
    : formatos("todas");

  const onClick: MenuProps["onClick"] = ({ key }) => {
    const [alcance, formato] = String(key).split(":") as [Alcance, FormatoResumen];
    void descargar(alcance, formato);
  };

  const deshabilitado = motivoDeshabilitado != null;

  return (
    // El tooltip sale con el mouse Y con el foco: el botón deshabilitado (aria-disabled) sí recibe foco por teclado.
    <Tooltip title={motivoDeshabilitado ?? undefined} trigger={["hover", "focus"]}>
      <span style={{ display: "inline-block" }}>
        {/* `trigger={[]}` y NO `disabled`: el `Dropdown` de AntD reenvía `disabled` al botón hijo, y entonces vuelve el `disabled`
            nativo (sin foco) que justamente se evita. Sin disparadores, el menú no se abre. */}
        <Dropdown menu={{ items, onClick }} trigger={deshabilitado ? [] : ["click"]}>
          {/* Sin la prop `loading` de Button: con ella el botón no recibe clics y el menú no abriría mientras se genera el
              PDF, y el Excel debe seguir disponible. El giro se pinta a mano.
              Sin `disabled` nativo (ver la cabecera): `aria-disabled` + la clase `ant-btn-disabled`, que es la que AntD
              usa para pintar el botón apagado (mismos colores, cursor y sin efectos de hover), así que no cambia el aspecto. */}
          <Button
            className={deshabilitado ? "ant-btn-disabled" : undefined}
            icon={cargando.size > 0 ? <LoadingOutlined /> : <DownloadOutlined />}
            aria-disabled={deshabilitado || undefined}
            aria-describedby={deshabilitado ? idMotivo : undefined}
            aria-haspopup="menu"
            aria-busy={cargando.size > 0}
          >
            Descargar resumen
          </Button>
        </Dropdown>
        {/* El motivo como texto real (el tooltip solo existe mientras se muestra). */}
        {deshabilitado && (
          <span id={idMotivo} style={SOLO_LECTOR}>
            {motivoDeshabilitado}
          </span>
        )}
        <span role="status" aria-live="polite" aria-atomic="true" style={SOLO_LECTOR}>
          {anuncio}
        </span>
      </span>
    </Tooltip>
  );
};

export default DescargarResumen;
